import { useState, useEffect, useCallback, useRef } from 'react';
import { post, get, del } from '../util/api';
import clientCache from '../util/clientCache';

/**
 * Hook for translating configuration assets (listingFields, listingTypes, etc.)
 * This ensures that when the language changes, config assets like enumOptions
 * (checkboxes for Attractions, etc.) are translated properly
 */
export const useTranslatedConfig = (config = {}, targetLanguage = 'en', options = {}) => {
  const {
    enabled = true,
    cacheTime = 60 * 60 * 1000, // 1 hour cache for config (changes less frequently)
  } = options;

  const [translatedConfig, setTranslatedConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cacheRef = useRef(new Map());
  const previousLanguageRef = useRef(targetLanguage);

  useEffect(() => {
    // Skip if not enabled or no config
    // NOTE: We translate even for English because Sharetribe Console config may be in Swedish
    if (!enabled || !config || (!config.listing?.listingFields && !config.listing?.listingTypes)) {
      setTranslatedConfig(null);
      return;
    }

    // Check if language changed
    const languageChanged = previousLanguageRef.current !== targetLanguage;
    if (languageChanged) {
      previousLanguageRef.current = targetLanguage;
    }

    // Check cache
    const cacheKey = `config-${targetLanguage}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < cacheTime) {
      console.log(`✅ Using cached translated config for ${targetLanguage}`);
      setTranslatedConfig(cached.data);
      return;
    }

    // Fetch translated config
    const fetchTranslatedConfig = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`🔄 Translating config assets to ${targetLanguage}...`);
        
        const response = await post('/api/translate-config', {
          config: {
            listingFields: config.listing?.listingFields ? { listingFields: config.listing.listingFields } : undefined,
            listingTypes: config.listing?.listingTypes ? { listingTypes: config.listing.listingTypes } : undefined,
          },
          targetLanguage,
        });

        if (response && response.success && response.data) {
          console.log(`✅ Successfully translated config to ${targetLanguage}`);
          
          // Unwrap the nested structure returned by API
          // API returns {listingFields: {listingFields: []}, listingTypes: {listingTypes: []}}
          // We need {listingFields: [], listingTypes: []} for components
          const unwrappedData = {
            listingFields: response.data.listingFields?.listingFields || [],
            listingTypes: response.data.listingTypes?.listingTypes || [],
          };
          
          // Cache the unwrapped result
          cacheRef.current.set(cacheKey, {
            data: unwrappedData,
            timestamp: Date.now(),
          });

          setTranslatedConfig(unwrappedData);
        } else {
          throw new Error('Invalid response from translate-config API');
        }
      } catch (err) {
        console.error('❌ Failed to translate config:', err);
        setError(err);
        setTranslatedConfig(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTranslatedConfig();
  }, [config, targetLanguage, enabled, cacheTime]);

  return { translatedConfig, loading, error };
};

/**
 * Advanced React hook for managing cached translated page assets
 * Provides automatic caching, cache invalidation, and optimistic updates
 */
export const useTranslatedAssets = (assetsData = {}, targetLanguage = 'en', options = {}) => {
  const {
    enabled = true,
    cacheTime = 30 * 60 * 1000, // 30 minutes default cache time
    staleTime = 5 * 60 * 1000,  // 5 minutes stale time
    retryOnError = true,
    maxRetries = 3,
    onError = null,
    onSuccess = null,
    refreshOnLanguageChange = true
  } = options;

  // State management
  const [state, setState] = useState({
    data: {},
    isLoading: false,
    isError: false,
    error: null,
    isFetching: false,
    lastFetch: null,
    cacheHitRate: 0,
    fromCache: 0,
    newTranslations: 0
  });

  // Refs for cleanup and memoization
  const abortControllerRef = useRef(null);
  const retryCountRef = useRef(0);
  const cacheRef = useRef(new Map());
  const previousLanguageRef = useRef(targetLanguage);

  /**
   * Check if cached data is still fresh
   */
  const isCacheStale = useCallback((cacheEntry) => {
    if (!cacheEntry) return true;
    const now = Date.now();
    return (now - cacheEntry.timestamp) > staleTime;
  }, [staleTime]);

  /**
   * Get cached data for assets
   */
  const getCachedData = useCallback(() => {
    const assetNames = Object.keys(assetsData);
    const cacheKey = `${targetLanguage}-${assetNames.join(',')}`;
    const cacheEntry = cacheRef.current.get(cacheKey);
    
    if (cacheEntry && !isCacheStale(cacheEntry)) {
      return cacheEntry.data;
    }

    // Fallback: check persistent client cache (survives page reloads)
    if (clientCache) {
      const persisted = clientCache.get(assetNames, targetLanguage);
      if (persisted) {
        // Re-populate in-memory ref for faster subsequent access
        cacheRef.current.set(cacheKey, { data: persisted.data, timestamp: persisted.cachedAt });
        return persisted.data;
      }
    }
    
    return null;
  }, [targetLanguage, assetsData, isCacheStale]);

  /**
   * Set cached data for assets
   */
  const setCachedData = useCallback((data) => {
    const assetNames = Object.keys(assetsData);
    const cacheKey = `${targetLanguage}-${assetNames.join(',')}`;
    cacheRef.current.set(cacheKey, {
      data,
      timestamp: Date.now()
    });

    // Persist to localStorage for cross-reload survival
    if (clientCache) {
      clientCache.set(assetNames, targetLanguage, data, { maxAge: cacheTime });
    }

    // Clean old cache entries (keep only last 50 entries)
    if (cacheRef.current.size > 50) {
      const entries = Array.from(cacheRef.current.entries());
      entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
      
      cacheRef.current.clear();
      entries.slice(0, 50).forEach(([key, value]) => {
        cacheRef.current.set(key, value);
      });
    }
  }, [targetLanguage, assetsData, cacheTime]);

  /**
   * Fetch translated assets from server
   */
  const fetchTranslatedAssets = useCallback(async (originalAssets, forceRefresh = false) => {
    if (!enabled || !Object.keys(originalAssets).length || !originalAssets) {
      return null;
    }

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      setState(prevState => ({
        ...prevState,
        isFetching: true,
        isError: false,
        error: null
      }));

      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedData = getCachedData();
        if (cachedData) {
          setState(prevState => ({
            ...prevState,
            data: cachedData.translatedAssets || {},
            isFetching: false,
            fromCache: Object.keys(cachedData.translatedAssets || {}).length,
            newTranslations: 0,
            cacheHitRate: 1
          }));
          
          if (onSuccess) {
            onSuccess(cachedData);
          }
          
          return cachedData;
        }
      }

      // Make API request
      const response = await post('/api/translate-assets', {
        assets: originalAssets,
        targetLanguage,
        forceRefresh,
        customTTL: cacheTime / 1000 // Convert to seconds
      }, {
        signal: abortControllerRef.current.signal,
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('✅ Translation API response:', response);

      if (!response.success) {
        throw new Error(response.error || 'Translation failed');
      }

      // Cache the result
      setCachedData(response);

      // Update state
      setState(prevState => ({
        ...prevState,
        data: response.translatedAssets || {},
        isLoading: false,
        isFetching: false,
        isError: false,
        error: null,
        lastFetch: Date.now(),
        fromCache: response.fromCache || 0,
        newTranslations: response.newTranslations || 0,
        cacheHitRate: response.cacheStats?.hitRate || 0
      }));

      // Reset retry count on success
      retryCountRef.current = 0;

      if (onSuccess) {
        onSuccess(response);
      }

      return response;

    } catch (error) {
      // Handle aborted requests
      if (error.name === 'AbortError') {
        return null;
      }

      console.error('Error fetching translated assets:', error);

      // Retry logic
      if (retryOnError && retryCountRef.current < maxRetries) {
        retryCountRef.current += 1;
        console.log(`Retrying translation request (${retryCountRef.current}/${maxRetries})`);
        
        // Exponential backoff
        const delay = Math.pow(2, retryCountRef.current) * 1000;
        setTimeout(() => {
          fetchTranslatedAssets(originalAssets, forceRefresh);
        }, delay);
        
        return null;
      }

      // Update error state
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isFetching: false,
        isError: true,
        error: error.message || 'Failed to fetch translations'
      }));

      if (onError) {
        onError(error);
      }

      throw error;
    }
  }, [
    enabled, targetLanguage, cacheTime, retryOnError, maxRetries,
    onError, onSuccess, getCachedData, setCachedData
  ]);

  /**
   * Get cached assets from server cache
   */
  const getCachedAssetsFromServer = useCallback(async () => {
    try {
      const assetNames = Object.keys(assetsData);
      const response = await get(`/api/translate-assets?language=${targetLanguage}&assets=${assetNames.join(',')}`, {
        signal: abortControllerRef.current?.signal
      });

      if (response && response.success && Object.keys(response.cached || {}).length > 0) {
        setCachedData({ translatedAssets: response.cached });
        setState(prevState => ({
          ...prevState,
          data: response.cached,
          fromCache: Object.keys(response.cached).length,
          cacheHitRate: response.cacheHitRate
        }));
        return response.cached;
      }
    } catch (error) {
      console.warn('Failed to get cached assets from server:', error.message);
    }
    
    return null;
  }, [targetLanguage, assetsData, setCachedData]);

  /**
   * Clear cache for specific assets/language
   */
  const clearCache = useCallback(async (serverCache = false) => {
    // Clear in-memory ref cache
    const assetNames = Object.keys(assetsData);
    const cacheKey = `${targetLanguage}-${assetNames.join(',')}`;
    cacheRef.current.delete(cacheKey);

    // Clear persistent client cache
    if (clientCache) {
      clientCache.delete(assetNames, targetLanguage);
    }

    // Clear server cache if requested
    if (serverCache) {
      try {
        await del(`/api/translate-assets?language=${targetLanguage}&assets=${assetNames.join(',')}`);
      } catch (error) {
        console.warn('Failed to clear server cache:', error.message);
      }
    }

    // Reset state
    setState(prevState => ({
      ...prevState,
      data: {},
      lastFetch: null,
      fromCache: 0,
      newTranslations: 0,
      cacheHitRate: 0
    }));
  }, [targetLanguage, assetsData]);

  /**
   * Refresh translations (force refetch)
   */
  const refresh = useCallback((originalAssets) => {
    return fetchTranslatedAssets(originalAssets, true);
  }, [fetchTranslatedAssets]);

  // Effect to handle language changes
  useEffect(() => {
    if (refreshOnLanguageChange && previousLanguageRef.current !== targetLanguage) {
      previousLanguageRef.current = targetLanguage;
      
      // Clear cache when language changes
      const assetNames = Object.keys(assetsData);
      const cacheKey = `${previousLanguageRef.current}-${assetNames.join(',')}`;
      cacheRef.current.delete(cacheKey);
      
      // Reset state for new language
      setState(prevState => ({
        ...prevState,
        data: {},
        isLoading: false,
        isFetching: false,
        lastFetch: null,
        fromCache: 0,
        newTranslations: 0
      }));
    }
  }, [targetLanguage, assetsData, refreshOnLanguageChange]);

  // Effect to listen for global language change events
  useEffect(() => {
    if (typeof window === 'undefined' || !window.emitter) return;

    const handleTranslationCacheCleared = (eventData) => {
      console.log('🧹 Translation cache cleared event received:', eventData);
      
      // Clear our local cache
      cacheRef.current.clear();
      
      // Reset state to trigger fresh fetch
      setState(prevState => ({
        ...prevState,
        data: {},
        isLoading: false,
        isFetching: false,
        lastFetch: null,
        fromCache: 0,
        newTranslations: 0,
        cacheHits: 0,
        cacheMisses: prevState.cacheMisses + 1 // Count as cache miss
      }));
    };

    const handleRefreshTranslatedAssets = (eventData) => {
      console.log('🔄 Refresh translated assets event received:', eventData);
      
      // Force refresh if we have assets to translate
      if (Object.keys(assetsData).length > 0 && enabled) {
        setState(prevState => ({
          ...prevState,
          isLoading: true,
          isFetching: true
        }));
      }
    };

    // Listen for language change events
    if (window.emitter && window.emitter.on) {
      window.emitter.on('translationCacheCleared', handleTranslationCacheCleared);
      window.emitter.on('refreshTranslatedAssets', handleRefreshTranslatedAssets);
    }

    return () => {
      // Cleanup event listeners
      if (window.emitter && window.emitter.off) {
        window.emitter.off('translationCacheCleared', handleTranslationCacheCleared);
        window.emitter.off('refreshTranslatedAssets', handleRefreshTranslatedAssets);
      }
    };
  }, [assetsData, enabled]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Auto-fetch when assets are provided
  useEffect(() => {
    if (enabled && Object.keys(assetsData).length > 0 && targetLanguage !== 'en') {
      fetchTranslatedAssets(assetsData);
    } else if (targetLanguage === 'en' || !enabled || Object.keys(assetsData).length === 0) {
      // For English or when disabled, just return the original assets
      setState(prevState => ({
        ...prevState,
        data: assetsData || {},
        isLoading: false,
        isFetching: false,
        isError: false,
        error: null
      }));
    }
  }, [enabled, assetsData, targetLanguage, fetchTranslatedAssets]);

  return {
    // Data
    translatedAssets: state.data,
    
    // Loading states
    loading: state.isLoading,
    isFetching: state.isFetching,
    
    // Error states
    isError: state.isError,
    error: state.error,
    
    // Cache info
    fromCache: state.fromCache,
    newTranslations: state.newTranslations,
    cacheHitRate: state.cacheHitRate,
    lastFetch: state.lastFetch,
    
    // Methods
    fetchTranslatedAssets,
    getCachedAssetsFromServer,
    clearCache,
    refresh,
    
    // Cache utilities
    isCacheStale: (assetName) => {
      const cacheKey = `${targetLanguage}-${assetName}`;
      const cacheEntry = cacheRef.current.get(cacheKey);
      return isCacheStale(cacheEntry);
    }
  };
};

export default useTranslatedAssets;